import { useCallback, useEffect, useState } from 'react';
import { ApiError, request } from '@/lib/api.js';
import { ReleaseCard, type ManifestSummary } from './releases/ReleaseCard.js';
import { GlassButton, PageHeader, useConfirm } from '@/components/ui/index.js';

interface ReleasesResponse {
  ok: true;
  live: ManifestSummary | null;
  draft: ManifestSummary | null;
}

export function ReleasesPage(): JSX.Element {
  const confirm = useConfirm();
  const [data, setData] = useState<ReleasesResponse | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [action, setAction] = useState<'publish' | 'discard' | null>(null);
  const [notice, setNotice] = useState<{ kind: 'success' | 'error'; text: string } | null>(null);

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    try {
      const res = await request<ReleasesResponse>('/admin/releases/renderer');
      setData(res);
    } catch (err) {
      setError(err instanceof Error ? err.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void load();
  }, [load]);

  async function handlePublish(): Promise<void> {
    if (data?.draft === null || data?.draft === undefined) return;
    const ok = await confirm({
      title: '推送热更新',
      message: `确认把 v${data.draft.version} 推送到所有用户吗？\n\n变更说明：${data.draft.changelog ?? '（无）'}\n\n他们重启 app 后就会看到。`,
      confirmLabel: '推送',
      tone: 'warning',
    });
    if (!ok) return;
    setAction('publish');
    setNotice(null);
    try {
      const res = await request<{ ok: true; version: string }>(
        '/admin/releases/renderer/publish',
        { method: 'POST' },
      );
      setNotice({ kind: 'success', text: `已发布 v${res.version}。所有用户下次启动 app 就会拿到。` });
      await load();
    } catch (err) {
      if (err instanceof ApiError && err.code === 'version_already_live') {
        setNotice({ kind: 'error', text: err.message });
      } else {
        setNotice({ kind: 'error', text: err instanceof Error ? err.message : '发布失败' });
      }
    } finally {
      setAction(null);
    }
  }

  async function handleDiscard(): Promise<void> {
    if (data?.draft === null || data?.draft === undefined) return;
    const ok = await confirm({
      title: '丢弃草稿',
      message: `确认丢弃草稿 v${data.draft.version}？\n\n变更说明：${data.draft.changelog ?? '（无）'}\n\n开发者需要重新构建并上传新草稿。`,
      confirmLabel: '丢弃',
      tone: 'danger',
    });
    if (!ok) return;
    setAction('discard');
    setNotice(null);
    try {
      await request<{ ok: true }>('/admin/releases/renderer/draft', { method: 'DELETE' });
      setNotice({ kind: 'success', text: '已丢弃草稿。' });
      await load();
    } catch (err) {
      setNotice({ kind: 'error', text: err instanceof Error ? err.message : '丢弃失败' });
    } finally {
      setAction(null);
    }
  }

  return (
    <div>
      <PageHeader
        title="热更新发布"
        subtitle="桌面客户端的 renderer 包 — 草稿审核后推到 live。"
        right={
          <GlassButton size="sm" variant="ghost" onClick={(): void => void load()} disabled={loading}>
            {loading ? '刷新中…' : '↻ 刷新'}
          </GlassButton>
        }
      />

      <p className="text-sm text-slate-400 mb-6 leading-relaxed">
        开发者本地跑 <code className="text-shark-300 bg-white/[0.04] px-1 rounded">pnpm renderer:publish</code> 后，
        新版本会
        <span className="text-shark-300 font-semibold">先进草稿</span>
        等你审核。审过 + 点「发布」之后，所有用户下次启动客户端就会拿到更新。
      </p>

      {error !== null && (
        <div className="text-sm text-rose-300 bg-rose-900/30 border border-rose-700/50 rounded-lg px-3 py-2 mb-4">
          {error}
        </div>
      )}

      {notice !== null && (
        <div
          className={`text-sm rounded-lg px-3 py-2 mb-4 ${
            notice.kind === 'success'
              ? 'text-felt-400 bg-felt-700/20 border border-felt-500/40'
              : 'text-rose-300 bg-rose-900/30 border border-rose-700/50'
          }`}
        >
          {notice.text}
        </div>
      )}

      <div className="grid lg:grid-cols-2 gap-4">
        {/* ============ 待审核草稿 ============ */}
        <ReleaseCard
          accent="amber"
          icon="📋"
          title="待审核草稿"
          statusBadge={
            data?.draft !== null && data?.draft !== undefined
              ? { text: '等审批', tone: 'amber' }
              : undefined
          }
          manifest={data?.draft ?? null}
          emptyTitle="暂无待审核草稿"
          emptyHint="开发者推送新版本后会出现在这里"
          versionColor="text-amber-200"
          changelogColor="text-slate-200"
          metaColor="text-slate-300"
          actions={
            data?.draft !== null && data?.draft !== undefined ? (
              <div className="flex flex-col sm:flex-row gap-2 mt-4">
                <GlassButton
                  variant="primary"
                  onClick={(): void => { void handlePublish(); }}
                  disabled={action !== null}
                  className="flex-1"
                >
                  {action === 'publish' ? '发布中…' : '✓ 审批并发布'}
                </GlassButton>
                <GlassButton
                  variant="danger"
                  onClick={(): void => { void handleDiscard(); }}
                  disabled={action !== null}
                >
                  {action === 'discard' ? '丢弃中…' : '✗ 丢弃草稿'}
                </GlassButton>
              </div>
            ) : undefined
          }
        />

        {/* ============ 线上版本 ============ */}
        <ReleaseCard
          accent="slate"
          icon="🌐"
          title="当前线上版本"
          statusBadge={
            data?.live !== null && data?.live !== undefined
              ? { text: '运行中', tone: 'felt' }
              : undefined
          }
          manifest={data?.live ?? null}
          emptyTitle="还没发过任何热更新"
          emptyHint="用户跑的是 .app bundled 渲染器"
          versionColor="text-shark-300"
          changelogColor="text-slate-300"
          metaColor="text-slate-400"
        />
      </div>

      <section className="mt-8 bg-white/[0.02] border border-white/[0.08] rounded-xl p-5">
        <h2 className="text-sm font-semibold text-slate-400 mb-3">📖 工作流</h2>
        <ol className="text-xs text-slate-500 space-y-1.5 list-decimal pl-4">
          <li>开发者改完 UI 代码后，在终端跑 <code className="text-shark-400 bg-white/[0.03] px-1 rounded">pnpm renderer:publish "改了什么"</code></li>
          <li>脚本自动构建 + Ed25519 签名 + 上传草稿到这里 ↑（左侧黄色卡片会出现）</li>
          <li>你（管理员）审核版本号 + 变更说明，点「审批并发布」</li>
          <li>右侧绿色卡片立刻显示新版本号；下面写明发布时间</li>
          <li>所有用户重启 app 后会自动收到「有新版本可用」banner，点立即生效完成更新</li>
        </ol>
      </section>
    </div>
  );
}
